import { LitElement, html, css, unsafeHTML } from "https://cdn.jsdelivr.net/gh/lit/dist@3/all/lit-all.min.js";

/**
 * Skio Universal Plan Picker - Buddy Drink Edition v2
 * @docs https://integrate.skio.com/
 *
 * Custom design based on Figma reference for Buddy Drink
 * - BEST SELLER header banner
 * - ÉCONOMISE 10% badge (static)
 * - 2-column benefits layout (12px font)
 * - 4 fixed gifts in horizontal scroll carousel
 * - Subscription selected by default
 */

const CHECK_ICON = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2.5" stroke="currentColor" width="11" height="11" style="flex-shrink:0"><path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>`;

const LABELS = {
  en: { subscribe: "Flexible subscription", onetime: "One time purchase", prepaid: "Prepaid" },
  fr: { subscribe: "Abonnement flexible", onetime: "Achat unique", prepaid: "Prépayée" },
  nl: { subscribe: "Flexibel abonnement", onetime: "Eenmalige aankoop", prepaid: "Vooruitbetaald" }
};

const SUBSCRIPTION_BENEFITS = {
  en: [
    "Save 10% on every order",
    "Pause, cancel or modify anytime",
    "Free delivery",
    "Exclusive deals & promos"
  ],
  fr: [
    "Économise 10% sur chaque commande",
    "Pause, annule ou modifie à tout moment",
    "Livraison gratuite",
    "Des promos exclusives"
  ],
  nl: [
    "Bespaar 10% op elke bestelling",
    "Pauzeer, annuleer of wijzig op elk moment",
    "Gratis levering",
    "Exclusieve kortingen & promos"
  ]
};

// ============================================================
// CADEAUX FIXES — Modifier ici uniquement
// ============================================================
// Remplace les URLs d'images par les vraies URLs (Shopify Assets ou CDN)
// Tu peux soit:
// - Hardcoder l'URL CDN: "https://cdn.shopify.com/s/files/.../image.png"
// - Passer par le Custom Liquid (voir le fichier .liquid)
function getGiftsTitle(locale) {
  const count = FIXED_GIFTS.length;
  const templates = {
    en: `${count} free gift${count > 1 ? 's' : ''}`,
    fr: `${count} cadeau${count > 1 ? 'x' : ''} gratuit${count > 1 ? 's' : ''}`,
    nl: `${count} gratis geschenk${count > 1 ? 'en' : ''}`
  };
  return templates[locale] || templates.en;
}

const FIXED_GIFTS = [
  {
    image: "https://cdn.shopify.com/s/files/1/0541/6589/7407/files/Pakcshot-gourde-orange2.jpg?v=1775737502",
    name: window.buddyGiftNames?.gourde || "buddy Gourde",
    comparePrice: window.buddyGiftPrices?.gourde || null,
    deliveryLabel: { en: "2ND DELIVERY", fr: "2ÈME COMMANDE", nl: "2E BESTELLING" }
  },
  {
    image: "https://cdn.shopify.com/s/files/1/0541/6589/7407/files/Buddyelectrolytepeachcitrus.jpg?v=1768499185",
    name: window.buddyGiftNames?.electrolytesPoudre || "Électrolytes en poudre",
    comparePrice: window.buddyGiftPrices?.electrolytesPoudre || null,
    deliveryLabel: { en: "3RD DELIVERY", fr: "3ÈME COMMANDE", nl: "3E BESTELLING" }
  },
  {
    image: "https://cdn.shopify.com/s/files/1/0541/6589/7407/files/HydratationFramboise_59f8f8d0-7acd-4691-a51f-8cbc4c0b80c2.jpg?v=1778493762",
    name: window.buddyGiftNames?.boissonElectrolytes || "Boisson aux électrolytes",
    comparePrice: window.buddyGiftPrices?.boissonElectrolytes || null,
    deliveryLabel: { en: "6TH DELIVERY", fr: "6ÈME COMMANDE", nl: "6E BESTELLING" }
  },
  {
    image: "https://cdn.shopify.com/s/files/1/0541/6589/7407/files/Tshirt-Vert.jpg?v=1773762095",
    name: window.buddyGiftNames?.tshirt || "T-shirt buddy",
    comparePrice: window.buddyGiftPrices?.tshirt || null,
    deliveryLabel: { en: "10TH DELIVERY", fr: "10ÈME COMMANDE", nl: "10E BESTELLING" }
  },
  {
    image: "https://cdn.shopify.com/s/files/1/0541/6589/7407/files/T-shirtbuddysport.jpg?v=1777553515",
    name: window.buddyGiftNames?.jersey || "Jersey buddy",
    comparePrice: window.buddyGiftPrices?.jersey || null,
    deliveryLabel: { en: "16TH DELIVERY", fr: "16ÈME COMMANDE", nl: "16E BESTELLING" }
  },
  {
    image: "https://cdn.shopify.com/s/files/1/0541/6589/7407/files/Buddy_Coin.jpg?v=1779972321",
    nameLocalized: { en: "Double buddy coin", fr: "Double buddy coin", nl: "Dubbele buddy coin" },
    comparePrice: null,
    deliveryLabel: { en: "EVERY DELIVERY", fr: "À CHAQUE COMMANDE", nl: "BIJ ELKE BESTELLING" }
  }
];

export class SkioPlanPicker extends LitElement {
  static properties = {
    key: { type: String },
    product: { type: Object },
    productHandle: { type: String },
    selectedVariant: { type: Object },
    options: { type: Object },
    moneyFormat: { type: String },
    quantity: { type: Number },
    rules: { type: Array },
    rule: { type: Object },
    cart: { type: Object },

    bestSellerLabel: { type: String },
    discountBadge: { type: String },

    availableSellingPlanGroups: { state: true },
    selectedSellingPlanGroup: { type: Object },
    selectedSellingPlan: { type: Object },

    disableUrl: { type: Boolean },
    debug: { type: Boolean }
  };

  static styles = [
    css`
      :host {
        display: block;
        width: 100%;
        max-width: 100%;
        min-width: 0;
        font-family: inherit;
        box-sizing: border-box;
      }

      *, *::before, *::after {
        box-sizing: border-box;
      }

      .skio-plan-picker {
        display: flex;
        flex-direction: column;
        gap: 12px;
        padding: 0;
        border: 0;
        margin: 0 0 1rem 0;
        font-size: 14px;
        color: #000;
        width: 100%;
        min-width: 0;
        max-width: 100%;
      }

      /* Critical: every container in the chain must allow shrinking
         so overflow-x on .gifts-scroll-container actually scrolls
         instead of pushing the parent wider */
      .group-container,
      .group-label,
      .group-topline {
        min-width: 0;
        max-width: 100%;
      }

      :host([layout="hidden"]) .skio-plan-picker {
        display: none;
      }

      .sr-only {
        position: absolute;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        width: 1px;
        height: 1px;
        margin: -1px;
        padding: 0;
        border: 0;
      }

      /* ============ GROUP CONTAINER (CARDS) ============ */
      .group-container {
        position: relative;
        background-color: #fff;
        border: 2px solid #1a1a1a;
        border-radius: 14px;
        transition: opacity 0.2s ease;
        overflow: hidden;
      }

      /* Inactive subscription card when one-time selected */
      .group-container.subscription-card:not(.group-container--selected) {
        opacity: 0.4;
      }

      /* Order: subscription first, one-time after */
      .group-container.subscription-card {
        order: 1;
      }
      .group-container.onetime-card {
        order: 2;
      }

      /* One-time card border color */
      .group-container.onetime-card {
        border-color: #1a1a1a;
        opacity: 0.4;
      }
      .group-container.onetime-card.group-container--selected {
        border-color: #373737;
        opacity: 1;
      }

      .group-input {
        position: absolute;
        opacity: 0;
        width: 0;
        height: 0;
      }

      .group-input:focus-visible ~ .group-label {
        outline: 2px #ccc solid;
        outline-offset: 4px;
      }

      .group-label {
        display: flex;
        flex-direction: column;
        cursor: pointer;
        padding: 0;
      }

      /* ============ TOP STRIP (BEST SELLER + DISCOUNT BADGE) ============ */
      .top-strip {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: space-between;
        background-color: #f3f3f3;
        padding: 6px 16px;
        min-height: 27px;
        border-radius: 12px 12px 0 0;
        overflow: hidden;
      }

      .best-seller-label {
        font-size: 13px;
        font-weight: 700;
        color: #000;
        letter-spacing: 0;
        line-height: 1;
      }

      .discount-badge {
        position: absolute;
        top: 0;
        right: 0;
        background-color: #ffcdcd;
        color: #ed476c;
        font-size: 11px;
        font-weight: 700;
        padding: 8px 14px;
        border-radius: 0 0 0 12px;
        letter-spacing: 0;
        line-height: 1;
      }

      /* ============ CARD HEADER (TITLE + PRICE) ============ */
      .group-topline {
        display: flex;
        align-items: center;
        width: 100%;
        gap: 10px;
        padding: 12px 16px 0 16px;
      }

      .onetime-card .group-topline {
        padding: 12px 16px;
      }

      .group-title {
        font-size: 14px;
        font-weight: 600;
        flex: 1;
      }

      .skio-price {
        display: flex;
        align-items: center;
        gap: 6px;
        font-size: 14px;
        font-weight: 600;
        line-height: 1;
      }

      .skio-price s {
        font-size: 14px;
        font-weight: 600;
        color: #cacaca;
        text-decoration: line-through;
      }

      .skio-bundle-price {
        display: flex;
        align-items: center;
        gap: 6px;
        font-size: 14px;
        font-weight: 600;
        line-height: 1;
      }
      .skio-bundle-price__compare {
        font-size: 14px;
        font-weight: 600;
        color: #cacaca;
        text-decoration: line-through;
      }
      .skio-bundle-price__current {
        font-size: 14px;
        font-weight: 600;
        color: #373737;
      }

      /* ============ RADIO BUTTON ============ */
      .skio-radio__container {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 14px;
        height: 14px;
        flex-shrink: 0;
      }

      .skio-radio-svg {
        width: 14px;
        height: 14px;
      }

      .skio-radio-outer {
        fill: none;
        stroke: #000;
        stroke-width: 1.5;
      }

      .skio-radio-inner {
        fill: #000;
        opacity: 0;
        transform-origin: center;
        transform: scale(0);
        transition: opacity 0.2s ease, transform 0.2s ease;
      }

      .group-container--selected .skio-radio-inner {
        opacity: 1;
        transform: scale(1);
      }

      /* ============ BENEFITS (2 COLUMNS) ============ */
      .benefits-grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        column-gap: 24px;
        row-gap: 12px;
        padding: 12px 16px 14px 16px;
      }

      .benefit-item {
        display: flex;
        align-items: flex-start;
        gap: 7px;
        font-size: 12px;
        line-height: 1.25;
        color: #000;
      }

      .benefit-icon {
        flex-shrink: 0;
        margin-top: 2px;
        color: #000;
      }

      /* Hide content when subscription not selected */
      .subscription-card:not(.group-container--selected) .benefits-grid,
      .subscription-card:not(.group-container--selected) .frequency-section,
      .subscription-card:not(.group-container--selected) .gifts-section {
        display: none;
      }

      /* Add vertical breathing room around "Abonne toi" row
         when the subscription card is in collapsed state (one-time selected) */
      .subscription-card:not(.group-container--selected) .group-topline {
        padding-top: 12px;
        padding-bottom: 12px;
      }

      /* ============ FREQUENCY DROPDOWN ============ */
      .frequency-section {
        padding: 0 16px 12px 16px;
      }

      .selling-plan-dropdown {
        appearance: none;
        -webkit-appearance: none;
        -moz-appearance: none;
        width: 100%;
        padding: 4px 30px 4px 10px;
        background-color: #fff;
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%23000' stroke-width='2'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' d='M19 9l-7 7-7-7' /%3E%3C/svg%3E");
        background-repeat: no-repeat;
        background-position: right 10px center;
        background-size: 12px;
        border: 1px solid #f3f3f3;
        border-radius: 4px;
        font-size: 12px;
        font-family: inherit;
        color: #000;
        cursor: pointer;
        height: 23px;
        line-height: 1;
      }

      .selling-plan-dropdown--one {
        background-image: none;
        pointer-events: none;
      }

      /* ============ GIFTS SECTION (HORIZONTAL SCROLL) ============ */
      .gifts-section {
        background-color: #f8f8f8;
        margin: 0 16px 14px 16px;
        border-radius: 8px;
        padding: 8px 0 8px 8px;
        /* Critical: prevent this section from pushing the card wider.
           min-width: 0 lets it shrink to fit, max-width caps it. */
        min-width: 0;
        max-width: calc(100% - 32px);
        overflow: hidden;
      }

      .gifts-title {
        font-size: 12px;
        font-weight: 600;
        color: #000;
        margin: 0 0 8px 4px;
      }

      .gifts-scroll-container {
        display: flex;
        gap: 8px;
        overflow-x: auto;
        overflow-y: hidden;
        scroll-snap-type: x mandatory;
        scroll-padding-left: 0;
        padding-right: 8px;
        -webkit-overflow-scrolling: touch;
        scrollbar-width: thin;
        scrollbar-color: #cacaca transparent;
        /* Critical: width 100% + min-width 0 makes the flex children
           overflow horizontally instead of expanding the container */
        width: 100%;
        min-width: 0;
      }

      /* Scrollbar styling (WebKit) */
      .gifts-scroll-container::-webkit-scrollbar {
        height: 4px;
      }
      .gifts-scroll-container::-webkit-scrollbar-track {
        background: transparent;
      }
      .gifts-scroll-container::-webkit-scrollbar-thumb {
        background: #cacaca;
        border-radius: 2px;
      }

      .gift-card {
        position: relative;
        background-color: #fff;
        border-radius: 6px;
        padding: 6px;
        display: flex;
        gap: 8px;
        align-items: center;
        flex-shrink: 0;
        width: 174px;
        scroll-snap-align: start;
        box-sizing: border-box;
      }

      .gift-image-wrapper {
        position: relative;
        flex-shrink: 0;
      }

      .gift-image {
        width: 72px;
        height: 72px;
        border-radius: 6px;
        object-fit: cover;
        display: block;
      }

      .gift-free-badge {
        position: absolute;
        top: 3px;
        left: 3px;
        background-color: #ffcdcd;
        color: #ed476c;
        font-size: 8px;
        font-weight: 700;
        padding: 2px 6px;
        border-radius: 16px;
        line-height: 1;
        letter-spacing: 0;
      }

      .gift-info {
        display: flex;
        flex-direction: column;
        justify-content: center;
        gap: 3px;
        flex: 1;
        min-width: 0;
        overflow: hidden;
      }

      .gift-name {
        font-size: 10px;
        font-weight: 600;
        color: #000;
        line-height: 1.2;
        /* Allow up to 2 lines for "Double buddy coin" without breaking layout */
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
        hyphens: auto;
        overflow-wrap: break-word;
        word-break: break-word;
      }

      .gift-compare-price {
        font-size: 10px;
        font-weight: 600;
        color: #cacaca;
        text-decoration: line-through;
        line-height: 1;
      }

      .gift-delivery-label {
        font-size: 9px;
        font-weight: 600;
        color: #ed476c;
        letter-spacing: 0;
        line-height: 1;
        margin-top: 4px;
      }

      /* ============ SETUP MODE (DEBUG) ============ */
      .setupMode {
        position: relative;
        color: black;
        padding: 1rem;
        display: flex;
        flex-direction: column;
        gap: 10px;
        margin-bottom: 1rem;
        font-size: 14px;
        border-style: solid;
        border-width: 4px;
        border-image: repeating-linear-gradient(-55deg, #000, #000 20px, #ffb101 20px, #ffb101 40px) 10;
      }

      .setupMode:after {
        content: "Setup Mode";
        position: absolute;
        top: 0;
        right: 0;
        background-color: black;
        color: white;
        padding: 0.25rem 1rem;
        font-size: 13px;
      }

      .setupMode .detection {
        display: grid;
        grid-template-columns: auto 1fr;
        gap: 10px;
      }

      .setupMode .detection small {
        grid-column: 1/-1;
      }

      /* ============================================================
         RESPONSIVE: DESKTOP (≥ 1008px)
         All fonts and spacings increase by +2px on larger screens
         ============================================================ */
      @media (min-width: 1008px) {
        .skio-plan-picker {
          gap: 14px;
          font-size: 16px;
        }

        /* Top strip */
        .top-strip {
          padding: 8px 18px;
          min-height: 31px;
        }
        .best-seller-label {
          font-size: 15px;
        }
        .discount-badge {
          font-size: 13px;
          padding: 10px 16px;
        }

        /* Card header */
        .group-topline {
          gap: 12px;
          padding: 14px 18px 0 18px;
        }
        .onetime-card .group-topline {
          padding: 14px 18px;
        }
        .group-title {
          font-size: 16px;
        }
        .skio-price {
          gap: 8px;
          font-size: 16px;
        }
        .skio-price s {
          font-size: 16px;
        }
        .skio-bundle-price {
          gap: 8px;
          font-size: 16px;
        }
        .skio-bundle-price__compare {
          font-size: 16px;
        }
        .skio-bundle-price__current {
          font-size: 16px;
        }

        /* Radio button */
        .skio-radio__container {
          width: 16px;
          height: 16px;
        }
        .skio-radio-svg {
          width: 16px;
          height: 16px;
        }

        /* Benefits */
        .benefits-grid {
          column-gap: 28px;
          row-gap: 14px;
          padding: 14px 18px 16px 18px;
        }
        .benefit-item {
          gap: 9px;
          font-size: 14px;
          line-height: 1.3;
        }
        /* Bump check icon size on desktop via inline width override */
        .benefit-icon svg {
          width: 13px !important;
          height: 13px !important;
        }

        /* Frequency dropdown */
        .frequency-section {
          padding: 0 18px 14px 18px;
        }
        .selling-plan-dropdown {
          padding: 6px 32px 6px 12px;
          background-position: right 12px center;
          background-size: 14px;
          font-size: 14px;
          height: 30px;
        }

        /* Gifts section */
        .gifts-section {
          margin: 0 18px 16px 18px;
          padding: 10px 0 10px 10px;
          max-width: calc(100% - 36px);
        }
        .gifts-title {
          font-size: 14px;
          margin: 0 0 10px 4px;
        }
        .gifts-scroll-container {
          gap: 10px;
          padding-right: 10px;
        }
        .gift-card {
          padding: 8px;
          gap: 10px;
          width: 136px;
          flex-direction: column;
          align-items: center;
        }
        .gift-image {
          width: 120px;
          height: 120px;
        }
        .gift-info {
          width: 100%;
          text-align: center;
        }
        .gift-free-badge {
          top: 4px;
          left: 4px;
          font-size: 10px;
          padding: 3px 7px;
        }
        .gift-info {
          gap: 4px;
        }
        .gift-name {
          font-size: 12px;
          hyphens: auto;
          overflow-wrap: break-word;
          word-break: break-word;
        }
        .gift-compare-price {
          font-size: 12px;
        }
        .gift-delivery-label {
          font-size: 11px;
          margin-top: 4px;
        }
      }
    `
  ];

  constructor() {
    super();
    this.debug = this.debug || window?.Shopify?.designMode;
    this.variantChanged = false;
    this.rules = [];
    this.rule = null;
    this.quantity = 1;
    this.bundleMode = false;
    this.bundleState = { items: [], totalCents: 0, totalCombinedCents: 0 };

    this.bestSellerLabel = window.buddyTranslations?.widget?.bestSeller || 'BEST SELLER';
    this.discountBadge = window.buddyTranslations?.widget?.discountBadge || 'ÉCONOMISE 10%';
  }

  async connectedCallback() {
    super.connectedCallback();
    this.log("Mounted");

    if (this.productHandle) {
      this.loading = true;
      this.fetchProduct(this.productHandle);
    }

    this.bindFormEvents();
    this.bindCartEvents();

    document.addEventListener('bundle:items-updated', (e) => {
      this.bundleMode = true;
      this.bundleState = e.detail;
      this.requestUpdate();
    });

    this.rules = await this.fetchRules();
    await this.getRule();
  }

  // ============ TEMPLATES ============

  radioTemplate() {
    return html`
      <div class="skio-radio__container">
        <svg class="skio-radio-svg" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <circle class="skio-radio-outer" cx="12" cy="12" r="10"></circle>
          <circle class="skio-radio-inner" cx="12" cy="12" r="6"></circle>
        </svg>
      </div>
    `;
  }

  sellingPlanDropdown(group) {
    return html`
      <select
        class="selling-plan-dropdown ${this.getAvailableSellingPlans(group).length == 1
          ? "selling-plan-dropdown--one"
          : ""}"
        @change=${(e) => this.selectSellingPlan(e.target.value)}
        @click=${(e) => e.stopPropagation()}>
        ${group
          ? this.getAvailableSellingPlans(group).map(
              (selling_plan) => html`
                <option
                  ?selected=${group.selected_selling_plan.id === selling_plan.id}
                  value=${selling_plan.id}>
                  ${selling_plan.name}
                </option>
              `
            )
          : ""}
      </select>
    `;
  }

  benefitsGrid() {
    const benefits = this.getLocalizedBenefits();
    return html`
      <div class="benefits-grid">
        ${benefits.map(
          (text) => html`
            <div class="benefit-item">
              <span class="benefit-icon">${unsafeHTML(CHECK_ICON)}</span>
              <span>${text}</span>
            </div>
          `
        )}
      </div>
    `;
  }

  giftsSection() {
    const locale = window?.Shopify?.locale?.split("-")[0] || "fr";
    const title = getGiftsTitle(locale);

    return html`
      <div class="gifts-section">
        <p class="gifts-title">${title}</p>
        <div class="gifts-scroll-container">
          ${FIXED_GIFTS.map(
            (gift) => html`
              <div class="gift-card">
                <div class="gift-image-wrapper">
                  <img class="gift-image" src="${gift.image}" alt="${gift.nameLocalized ? (gift.nameLocalized[locale] ?? gift.nameLocalized.fr) : gift.name}" />
                  <span class="gift-free-badge">${window.buddyTranslations?.widget?.freeBadge || 'GRATUIT'}</span>
                </div>
                <div class="gift-info">
                  <span class="gift-name" lang="${locale}">${gift.nameLocalized ? (gift.nameLocalized[locale] ?? gift.nameLocalized.fr) : gift.name}</span>
                  ${gift.comparePrice
                    ? html`<span class="gift-compare-price">${gift.comparePrice}</span>`
                    : ""}
                  <span class="gift-delivery-label">${gift.deliveryLabel[locale] ?? gift.deliveryLabel.fr}</span>
                </div>
              </div>
            `
          )}
        </div>
      </div>
    `;
  }

  invalidIcon() {
    return html`<svg width="24" height="24" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="#D22B2B"><path stroke-linecap="round" stroke-linejoin="round" d="M9.75 9.75l4.5 4.5m0-4.5l-4.5 4.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>`;
  }

  validIcon() {
    return html`<svg width="24" height="24" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="#0BDA51"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>`;
  }

  setupMode() {
    return html`
      <div class="setupMode">
        Skio Plan Picker
        <small>This will not appear on the live site.</small>
        <small>Key: ${this.key}</small>
        <div class="detection">${this.product ? this.validIcon() : this.invalidIcon()} Product</div>
        <div class="detection">${this.selectedVariant ? this.validIcon() : this.invalidIcon()} Selected Variant</div>
        <div class="detection">${this.form ? this.validIcon() : this.invalidIcon()} Form</div>
        <div class="detection">${this.variantInput ? this.validIcon() : this.invalidIcon()} Variant Input</div>
        <div class="detection">
          ${this.variantChanged ? this.validIcon() : this.invalidIcon()} Variant Change Detection
          <small>Please attempt to change the selected variant.</small>
        </div>
      </div>
    `;
  }

  render() {
    if (!this.viable() || this.options?.layout === "hidden") {
      return this.debug ? this.setupMode() : "";
    }

    if (
      this.selectedVariant.selling_plan_allocations.length === 0 &&
      !this.options?.show_without_subscription
    ) {
      return this.debug ? this.setupMode() : "";
    }

    return html`
      ${this.debug ? this.setupMode() : null}

      <fieldset class="skio-plan-picker" role="radiogroup" aria-labelledby="skio-plan-picker-legend">
        <legend id="skio-plan-picker-legend" class="sr-only">Select purchase option</legend>

        <!-- SUBSCRIPTION CARDS -->
        ${this.availableSellingPlanGroups?.length
          ? this.availableSellingPlanGroups.map(
              (group, index) => html`
                <div
                  class="group-container subscription-card ${this.selectedSellingPlanGroup == group
                    ? "group-container--selected"
                    : ""}"
                  @click=${() => this.selectSellingPlanGroup(group)}>

                  <div class="top-strip">
                    <span class="best-seller-label">${this.bestSellerLabel}</span>
                    <span class="discount-badge">${this.discountBadge}</span>
                  </div>

                  <input
                    id="group-${index}-${this.key}"
                    class="group-input"
                    name="skio-group-${this.key}"
                    type="radio"
                    value="${group.id}"
                    @change=${() => this.selectSellingPlanGroup(group)}
                    ?checked=${this.selectedSellingPlanGroup == group} />

                  <label class="group-label" for="group-${index}-${this.key}">
                    <div class="group-topline">
                      ${this.radioTemplate()}
                      <div class="group-title">
                        ${group.name === "Prepaid"
                          ? (this.getLocalizedLabels().prepaid ?? group.name)
                          : this.getLocalizedLabels().subscribe}
                      </div>
                      <div class="skio-price" aria-live="polite" style="${this.bundleMode ? 'display:none' : ''}">
                        ${this.selectedVariant?.compare_at_price > this.selectedVariant.price ||
                        this.selectedVariant.price > this.unitPrice(group.selected_selling_plan)
                          ? html`<s aria-hidden="true">${this.variantPrice(true)}</s>`
                          : ""}
                        ${this.price(group.selected_selling_plan)}
                      </div>
                      ${this.bundleMode ? html`
                        <div class="skio-bundle-price" aria-live="polite">
                          <s class="skio-bundle-price__compare">${this.money(this.bundleState.totalCents)}</s>
                          <span class="skio-bundle-price__current">${this.money(window.BundleState?.calculateBundlePriceWithCartContext(this.bundleState.totalCents, this.bundleState.totalCombinedCents, true) || 0)}</span>
                        </div>` : ""}
                    </div>
                  </label>

                  ${this.benefitsGrid()}

                  <div class="frequency-section">
                    ${this.sellingPlanDropdown(group)}
                  </div>
                  ${this.giftsSection()}

                  ${""}
                </div>
              `
            )
          : ""}

        <!-- ONE-TIME PURCHASE CARD -->
        ${!this.product.requires_selling_plan
          ? html`
              <div
                class="group-container onetime-card ${this.selectedSellingPlanGroup == null
                  ? "group-container--selected"
                  : ""}"
                @click=${() => this.selectSellingPlanGroup(null)}>
                <input
                  id="one-time-${this.key}"
                  class="group-input"
                  name="skio-group-${this.key}"
                  type="radio"
                  value="One time purchase"
                  ?checked=${!this.selectedSellingPlanGroup}
                  @change=${() => this.selectSellingPlanGroup(null)} />

                <label class="group-label" for="one-time-${this.key}">
                  <div class="group-topline">
                    ${this.radioTemplate()}
                    <div class="group-title">${this.getLocalizedLabels().onetime}</div>
                    <div class="skio-price" aria-live="polite" style="${this.bundleMode ? 'display:none' : ''}">
                      ${this.variantPrice()}
                    </div>
                    ${this.bundleMode ? (() => {
                      const currentPrice = window.BundleState?.calculateBundlePriceWithCartContext(this.bundleState.totalCents, this.bundleState.totalCombinedCents, false) || 0;
                      const hasDiscount = currentPrice < this.bundleState.totalCents;
                      return html`
                        <div class="skio-bundle-price" aria-live="polite">
                          ${hasDiscount ? html`<s class="skio-bundle-price__compare">${this.money(this.bundleState.totalCents)}</s>` : ""}
                          <span class="skio-bundle-price__current">${this.money(currentPrice)}</span>
                        </div>`;
                    })() : ""}
                  </div>
                </label>
              </div>
            `
          : ""}
      </fieldset>
    `;
  }

  // ============ SKIO LOGIC (UNCHANGED) ============

  async fetchRules() {
    if (sessionStorage.getItem("skio-discount-rules")) {
      return JSON.parse(sessionStorage.getItem("skio-discount-rules"));
    } else {
      const rules = await fetch(
        `https://api.skio.com/storefront-http/get-rules-by-domain-or-hostname?domain=${window?.Shopify?.shop}`
      )
        .then((response) => response.json())
        .then((response) => {
          return response.rules
            .filter((rule) => rule.type !== "surpriseDelight")
            .sort((a, b) => a.minQuantityToDiscount - b.minQuantityToDiscount);
        });
      sessionStorage.setItem("skio-discount-rules", JSON.stringify(rules));
      return rules;
    }
  }

  eligibleItemCount(rule) {
    const eligibleQuantity = rule?.productVariantIds.some((gid) => gid.includes(this.selectedVariant.id))
      ? this.quantity : 0;
    if (!this.cart?.items) return eligibleQuantity;
    const cartItems = Number(
      this.cart?.items.reduce(
        (aggregate, item) =>
          rule?.productVariantIds.some(
            (gid) => gid.includes(item.id) && item.selling_plan_allocation && gid.includes(this.selectedVariant.id)
          ) ? aggregate + item.quantity : aggregate,
        0
      )
    );
    return cartItems + eligibleQuantity;
  }

  getRule() {
    let largestMinQuantity = { value: -1, index: -1 };
    this.rules.forEach((rule, index) => {
      if (rule.code && largestMinQuantity.value < rule.minQuantityToDiscount) {
        if (rule.minQuantityToDiscount <= this.eligibleItemCount(rule)) {
          largestMinQuantity.value = rule.minQuantityToDiscount;
          largestMinQuantity.index = index;
        }
      }
    });
    this.rule = largestMinQuantity.index > -1 ? this.rules[largestMinQuantity.index] : null;
  }

  bindCartEvents() {
    document.addEventListener("CartUpdated", (event) => {
      this.cart = event.detail;
    });
  }

  bindFormEvents() {
    this.form = document.querySelector(`#${this.options?.form_id}`) || this.closest('form[action*="/cart/add"]');
    if (!this.form) return;
    this.variantInput = this.form.querySelector('[name="id"]');
    if (this.variantInput) {
      this.variantInput.addEventListener("change", (e) => {
        this.selectedVariant = this.product.variants.find((variant) => variant.id == e.target.value);
        this.variantChanged = true;
      });
    }

    // ============ QUANTITY HANDLING ============
    // The quantity input in Impact (Maestrooo) theme is OUTSIDE the form,
    // linked via the [form="..."] HTML attribute. So we need to search the
    // whole document for inputs that target our form by ID.
    //
    // Strategy:
    // 1. First try inside the form (works on most themes: Dawn, Sense, etc.)
    // 2. Fallback: search document-wide for [name="quantity"][form="<formId>"]
    // 3. Last resort: any [name="quantity"] input on the page

    let $quantityInput = this.form.querySelector('[name="quantity"]');

    if (!$quantityInput && this.form.id) {
      // Impact theme: input is outside form, linked via [form="..."] attribute
      $quantityInput = document.querySelector(`[name="quantity"][form="${this.form.id}"]`);
    }

    if (!$quantityInput) {
      // Ultimate fallback
      $quantityInput = document.querySelector('[name="quantity"]');
    }

    if ($quantityInput) {
      this.quantity = Number($quantityInput.value);
      this.log("Quantity input found:", $quantityInput);

      const syncQuantity = () => {
        const newQty = Number($quantityInput.value);
        if (newQty !== this.quantity && !isNaN(newQty) && newQty > 0) {
          this.log("Quantity changed:", this.quantity, "->", newQty);
          this.quantity = newQty;
        }
      };

      // Standard input/change events
      $quantityInput.addEventListener("input", syncQuantity);
      $quantityInput.addEventListener("change", syncQuantity);

      // MutationObserver for programmatic value changes (Impact's quantity-input
      // custom element may set .value via JS without firing events)
      const quantityObserver = new MutationObserver(syncQuantity);
      quantityObserver.observe($quantityInput, {
        attributes: true,
        attributeFilter: ["value"]
      });

      // Listen to clicks on the parent <quantity-selector> custom element
      // (Impact theme): +/- buttons trigger value change without dispatching
      // input/change events on the input itself.
      const $quantityWrapper = $quantityInput.closest("quantity-selector, [class*='quantity-selector'], .quantity-input, .product-info__quantity-selector");
      if ($quantityWrapper) {
        $quantityWrapper.addEventListener("click", () => {
          // Defer to next tick so the input value has time to update
          setTimeout(syncQuantity, 0);
          // Also retry after a slightly longer delay (some themes debounce)
          setTimeout(syncQuantity, 100);
        });
      }
    } else {
      this.error("No quantity input found in document");
    }
  }

  updated(changed) {
    if (!this.viable()) return;

    if (changed.has("product") && this.product) {
      this.key = this.key ? this.key : this.product.id;
    }

    if (changed.has("selectedVariant") && this.selectedVariant) {
      const filteredSellingPlanGroups = this.product.selling_plan_groups.filter(
        (selling_plan_group) =>
          selling_plan_group.app_id === "SKIO" &&
          !selling_plan_group.name.toLowerCase().includes("hidden-") &&
          !selling_plan_group.name.toLowerCase().includes("dynamic box subscription")
      );

      this.availableSellingPlanGroups = filteredSellingPlanGroups.filter((selling_plan_group) =>
        selling_plan_group.selling_plans.some((selling_plan) =>
          this.selectedVariant.selling_plan_allocations.some(
            (selling_plan_allocation) => selling_plan_allocation.selling_plan_id === selling_plan.id
          )
        )
      );

      if (this.options?.combine_groups) {
        this.availableSellingPlanGroups = [{
          name: this.options?.combined_group_name,
          selling_plans: this.availableSellingPlanGroups.flatMap((group) => group.selling_plans),
          id: "combined_group",
          app_id: "SKIO"
        }];
      }

      if (this.availableSellingPlanGroups?.length) {
        const url = new URL(window.location.href);
        const urlSelectedSellingPlanId = url.searchParams.get("selling_plan");

        this.availableSellingPlanGroups.forEach((group) => {
          const availableSellingPlans = this.getAvailableSellingPlans(group);
          const urlSelectedSellingPlan = availableSellingPlans.find((plan) => plan.id == urlSelectedSellingPlanId);
          const nameSelectedSellingPlan = availableSellingPlans.find((plan) => plan.name === this.selectedSellingPlan?.name);
          const defaultSellingPlan =
            this.options?.default_subscription && this.options.default_subscription.trim() !== ""
              ? availableSellingPlans.find((plan) =>
                  plan.name.toLowerCase().includes(this.options.default_subscription.toLowerCase())
                ) : null;

          group.selected_selling_plan =
            urlSelectedSellingPlan || nameSelectedSellingPlan || defaultSellingPlan || availableSellingPlans[0];

          const isAnyPlanSelected = urlSelectedSellingPlan || nameSelectedSellingPlan || defaultSellingPlan;
          group.selected = !!isAnyPlanSelected;
        });

        // Subscription selected by default (Buddy Drink)
        let selectedSellingPlanGroup =
          this.availableSellingPlanGroups.find((group) => group.selected) || this.availableSellingPlanGroups[0];
        let selectedSellingPlanId = selectedSellingPlanGroup.selected_selling_plan.id;
        this.selectSellingPlan(selectedSellingPlanId);
      } else {
        this.selectSellingPlanGroup(null);
      }
    }

    if (changed.has("selectedSellingPlan")) {
      this.dispatchEvent(new CustomEvent(`skio::update-selling-plan`, {
        bubbles: true, composed: true,
        detail: { variant: this.selectedVariant, sellingPlan: this.selectedSellingPlan, key: this.key }
      }));
      this.dispatchEvent(new CustomEvent("change", { detail: { value: this.selectedSellingPlan?.id } }));
    }

    this.updateForm();
    this.updateExternalPrice();
    if (!this.disableUrl) this.updateURLParams();
    if (changed.has("quantity")) this.getRule();
    if (changed.has("cart")) this.getRule();
    this.updateExternalElements();
  }

  updateExternalElements() {
    // External [skio-price] elements show UNIT price (consistent with theme price display)
    document.querySelectorAll(`[skio-price][skio-key="${this.key}"]`).forEach((el) => {
      el.innerHTML = this.selectedSellingPlan
        ? this.money(this.unitPrice(this.selectedSellingPlan))
        : this.money(this.selectedVariant.price);
    });
    document.querySelectorAll(`[skio-subscription-content]`).forEach((el) => {
      el.style.display = this.selectedSellingPlan ? "block" : "none";
    });
  }

  log(...args) {
    args.unshift(`%c[skio plan picker][${this.key}]`, "color: #8770f2;");
    console.log.apply(console, args);
  }

  error(...args) {
    args.unshift(`%c [skio plan picker][${this.key}]`, "color: #ff0000");
    console.error.apply(console, args);
  }

  viable() {
    const errors = [];
    if (!this.querySelector("input[name=selling_plan]")) errors.push("Missing selling plan input");
    if (!this.product) errors.push("No product found.");
    if (!this.selectedVariant) errors.push("No variant found.");
    if (!this.form) errors.push("No form found.");
    if (!this.variantInput) errors.push("No variant input found.");
    if (errors.length == 0) return true;
    this.error("Errors", errors);
    return false;
  }

  getAvailableSellingPlans(group) {
    return group.selling_plans.filter((selling_plan) =>
      this.selectedVariant.selling_plan_allocations.some(
        (selling_plan_allocation) => selling_plan_allocation.selling_plan_id === selling_plan.id
      )
    );
  }

  selectSellingPlanGroup(group) {
    this.selectedSellingPlanGroup = group;
    this.selectedSellingPlan = group?.selected_selling_plan;
  }

  selectSellingPlan(selling_plan_id) {
    const selectedGroup = this.availableSellingPlanGroups.find((group) =>
      group.selling_plans.some((plan) => plan.id == selling_plan_id)
    );
    const selectedSellingPlan = selectedGroup.selling_plans.find((plan) => plan.id == selling_plan_id);
    selectedGroup.selected_selling_plan = selectedSellingPlan;
    this.selectedSellingPlanGroup = selectedGroup;
    this.selectedSellingPlan = selectedSellingPlan;
  }

  updateExternalPrice() {
    if (!this.options?.external_price_selector) return;
    // External price (e.g. main PDP price in theme header) shows UNIT price,
    // not multiplied by quantity. The widget itself shows the total.
    document.querySelectorAll(this.options?.external_price_selector).forEach((el) => {
      this.selectedSellingPlan
        ? (el.innerHTML = this.money(this.unitPrice(this.selectedSellingPlan)))
        : (el.innerHTML = this.money(this.selectedVariant.price));
    });
  }

  getLocalizedBenefits() {
    const locale = window?.Shopify?.locale?.split("-")[0] || "fr";
    return SUBSCRIPTION_BENEFITS[locale] ?? SUBSCRIPTION_BENEFITS["fr"];
  }

  getLocalizedLabels() {
    const locale = window?.Shopify?.locale?.split("-")[0] || "fr";
    return LABELS[locale] ?? LABELS["fr"];
  }

  discount(selling_plan) {
    if (!selling_plan) return { percent: 0, amount: 0 };
    return this.getDiscountFromPriceAdjustment(selling_plan, selling_plan.price_adjustments[0]);
  }

  getDiscountFromPriceAdjustment(selling_plan, price_adjustment) {
    const discount = { percent: 0, amount: 0, absolute: 0 };
    let multiplier = 1;
    let sellingPlanGroup = this.product.selling_plan_groups.find((group) =>
      group.selling_plans.some((plan) => plan.id == selling_plan.id)
    );
    if (sellingPlanGroup?.name === "Prepaid") {
      const intervalDateMatches = selling_plan.name.match(/\b\d+\s*(days|weeks|months|years)\b/gi);
      if (intervalDateMatches?.length) {
        const intervals = intervalDateMatches[0].match(/\d+/g);
        multiplier = intervals[0] / (intervals[1] || 1);
        multiplier = multiplier > 1 ? multiplier : 1;
      }
    }
    const price = this.selectedVariant.price;
    const compareAtPrice =
      this.selectedVariant.compare_at_price && this.selectedVariant.compare_at_price > price
        ? this.selectedVariant.compare_at_price : price;
    switch (price_adjustment.value_type) {
      case "percentage":
        discount.percent = price_adjustment.value;
        discount.absolute = Math.round((price * price_adjustment.value) / 100.0);
        discount.amount = Math.round((price * price_adjustment.value) / 100.0);
        break;
      case "fixed_amount":
        discount.percent = Math.round(((price_adjustment.value * 1.0) / price) * 100.0);
        discount.absolute = price_adjustment.value;
        discount.amount = price_adjustment.value;
        break;
      case "price":
        discount.percent = Math.round(
          (((compareAtPrice * multiplier - price_adjustment.value) * 1.0) / (compareAtPrice * multiplier)) * 100.0
        );
        discount.absolute = compareAtPrice * multiplier - price_adjustment.value;
        discount.amount = price - price_adjustment.value;
        break;
    }
    return discount;
  }

  money(cents, formatString = this.moneyFormat) {
    const placeholderRegex = /{{\s*(\w+)\s*}}/;
    function formatWithDelimiters(number, precision = 2, thousands = ",", decimal = ".") {
      if (isNaN(number) || number == null) return "0";
      const numString = (number / 100.0).toFixed(precision);
      const parts = numString.split(".");
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1" + thousands);
      const cents = parts[1] ? decimal + parts[1] : "";
      return dollars + cents;
    }
    return formatString.replace(placeholderRegex, (match, placeholder) => {
      switch (placeholder) {
        case "amount": return formatWithDelimiters(cents, 2);
        case "amount_no_decimals": return formatWithDelimiters(cents, 0);
        case "amount_with_comma_separator": return formatWithDelimiters(cents, 2, ".", ",");
        case "amount_no_decimals_with_comma_separator": return formatWithDelimiters(cents, 0, ".", ",");
        case "amount_with_apostrophe_separator": return formatWithDelimiters(cents, 2, "'", ".");
        case "amount_no_decimals_with_space_separator": return formatWithDelimiters(cents, 0, " ");
        case "amount_with_space_separator": return formatWithDelimiters(cents, 2, " ", ",");
        case "amount_with_period_and_space_separator": return formatWithDelimiters(cents, 2, " ", ".");
        default: return match;
      }
    });
  }

  price(selling_plan, formatted = true) {
    // Unit price (with rule + selling plan discount applied)
    const unitPrice = this.unitPrice(selling_plan);

    // Multiply by quantity to show total price in the widget
    const totalPrice = unitPrice * (this.quantity || 1);

    return formatted ? this.money(totalPrice) : totalPrice;
  }

  /**
   * Returns the unit price with rule + selling plan discount applied,
   * BEFORE quantity multiplication. Used internally for price comparisons
   * (e.g. deciding whether to show the compare-at strikethrough).
   */
  unitPrice(selling_plan) {
    if (this.rule) {
      return this.selectedVariant.price - this.discount(selling_plan).amount - this.selectedVariant.price * (this.rule.discountAmount / 100);
    }
    return this.selectedVariant.price - this.discount(selling_plan).amount;
  }

  /**
   * Returns the variant price multiplied by current quantity, formatted.
   * Used for: one-time purchase price, compare_at_price strikethrough.
   * Falls back to variant.price if compare_at_price not set.
   */
  variantPrice(useCompareAt = false) {
    const basePrice = useCompareAt && this.selectedVariant.compare_at_price
      ? this.selectedVariant.compare_at_price
      : this.selectedVariant.price;
    return this.money(basePrice * (this.quantity || 1));
  }

  updateForm() {
    let $sellingPlan = this.querySelector("input[name=selling_plan]");
    if ($sellingPlan) {
      $sellingPlan.value = this.selectedSellingPlan ? this.selectedSellingPlan.id : "";
      $sellingPlan.dispatchEvent(new Event("change"));
    }
  }

  updateURLParams() {
    const url = new URL(window.location.href);
    if (this.selectedSellingPlan) {
      url.searchParams.set("selling_plan", this.selectedSellingPlan.id);
      window.history.replaceState({}, "", url.href);
    } else {
      url.searchParams.delete("selling_plan");
      window.history.replaceState({}, "", url.href);
    }
  }

  addToCart(quantity) {
    const items = [{
      id: this.selectedVariant.id,
      quantity: quantity,
      ...(this.selectedSellingPlan && { selling_plan: this.selectedSellingPlan?.id })
    }];
    fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ items })
    })
      .then((response) => response.json())
      .then((response) => {
        this.dispatchEvent(new CustomEvent(`skio::added-to-cart`, {
          bubbles: true, composed: true, detail: { response, key: this.key }
        }));
      })
      .catch((error) => this.error(`SKIO ${this.key} error adding item to cart: `, error));
  }

  fetchProduct = (handle) => {
    return fetch(`/products/${handle}.js`)
      .then((response) => response.json())
      .then((product) => {
        this.product = product;
        this.selectedVariant = product.variants[0];
        return product;
      });
  };
}

if (!customElements.get("skio-plan-picker")) {
  customElements.define("skio-plan-picker", SkioPlanPicker);
}

if (window) window.SkioPlanPicker = SkioPlanPicker;